<template>
  <div>
    <BCard v-if="error" variant="danger" class="mb-3">
      {{ error }}
    </BCard>

    <form id="payment-form" class="mt-4">
      <div class="border-b mb-6 pb-6">
        <div class="sm:flex items-center sm:space-x-3 rtl:space-x-reverse mb-3">
          <UFormGroup :label="$t('general.firstName')" class="w-full sm:w-1/2">
            <UInput v-model="billingContact.givenName" type="text" :placeholder="$t('general.firstName')" flat />
          </UFormGroup>

          <UFormGroup :label="$t('general.lastName')" class="w-full sm:w-1/2">
            <UInput v-model="billingContact.familyName" type="text" :placeholder="$t('general.lastName')" flat />
          </UFormGroup>
        </div>

        <UFormGroup :label="$t('general.address')" class="w-full  mb-3">
          <UInput v-model="billingContact.addressLines[0]" type="text" :placeholder="$t('general.address')" flat />
        </UFormGroup>

        <div class="sm:flex items-center sm:space-x-3  rtl:space-x-reverse mb-3">
          <UFormGroup :label="$t('general.city')" class="w-full sm:w-1/2">
            <UInput v-model="billingContact.city" type="text" :placeholder="$t('general.city')" flat />
          </UFormGroup>
          <UFormGroup :label="$t('general.state')" class="w-full sm:w-1/2">
            <UInput v-model="billingContact.region" type="text" :placeholder="$t('general.state')" flat />
          </UFormGroup>
        </div>
      </div>

      <div v-if="integration.data.payment_option_afterpay" id="afterpay-button" class="w-full mb-2" @click="process('afterpay', false)" />

      <div v-if="integration.data.payment_option_google_pay" id="google-pay-button" class="w-full mb-2" :class="{ 'hidden' : !googlePay }" @click="process('googlePay', true)" />

      <div v-if="integration.data.payment_option_apple_pay" id="apple-pay-button" class="w-full mb-2" @click="process('applePay', true)" />

      <!--<div v-if="integration.data.payment_option_card" id="card-container" class="mt-4" :class="{ 'hidden' : !card }"  />-->

      <div id="card-container" class="mt-4" />
    </form>

    <div id="payment-status-container" />

    <UButton block size="lg" :loading="processing" :disabled="disabled || processing" @click="process('card')">
      {{ confirmButtonTitle }}
    </UButton>

    <div v-if="loading" class="text-center">
      <BSpinner label="Loading" class="bookapp-action-tint-color" />
    </div>
  </div>
</template>

<script setup lang="ts">
import Bowser from 'bowser'

interface Props {
  label: string
  amount: number
  customer: any
  confirmButtonTitle: string
  integration: any
  disabled: boolean
  action: Function
  intent?: string
}

const props = withDefaults(defineProps<Props>(), {
  intent: 'charge'
})

const emit = defineEmits(['callback'])

let card: any;
let googlePay: any;
let applePay: any;
let afterpay: any;

const payments = ref()
const loading = ref(true)
const processing = ref(false)
const billingContact = ref({
  familyName: '',
  givenName: '',
  email: '',
  addressLines: [''],
  city: '',
  region: ''
})

const error = ref()

onMounted(() => {
  if (props.customer) {
    if (props.customer.address) {
      billingContact.value.addressLines[0] = props.customer.address
    }
    if (props.customer.address2) {
      billingContact.value.addressLines[1] = props.customer.address2
    }

    if (props.customer.suburb && props.customer.city) {
      billingContact.value.city = props.customer.suburb + ', ' + props.customer.city
    } else if (props.customer.suburb) {
      billingContact.value.city = props.customer.suburb
    } else if (props.customer.city) {
      billingContact.value.city = props.customer.city
    }

    if (props.customer.region) {
      billingContact.value.region = props.customer.region
    }

    if (props.customer.firstName) {
      billingContact.value.givenName = props.customer.firstName
    }

    if (props.customer.lastName) {
      billingContact.value.familyName = props.customer.lastName
    }

    if (props.customer.email) {
      billingContact.value.email = props.customer.email
    }
  }

  load()
})

// Create square token and process payment
const process = async (paymentMethod: string) => {
  processing.value = true

  try {
    let method = card

    if (paymentMethod === 'googlePay') {
      method = googlePay
    } else if (paymentMethod === 'applePay') {
      method = applePay
    } else if (paymentMethod === 'afterpay') {
      method = afterpay
    }

    const squareToken = await tokenize(method)

    const verificationToken = await verifyBuyer(payments.value, squareToken)

    const params = {
      squareToken
    }

    if (verificationToken) {
      params.verificationToken = verificationToken
    }

    emit('callback', { success: true, params })
  } catch (e) {
    console.log(e)
    // this.$sentry.captureException(e)
    error.value = e
  }

  processing.value = false
}

// Load Square Web Payment SDK script if not already loaded
const load = () => {
  if (window.Square) {
    nextTick(() => {
      initialize()
    })

  } else {
    const data = props.integration.data
    const sandbox = data.sandbox || false

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = `https://${sandbox ? 'sandbox.' : ''}web.squarecdn.com/v1/square.js`
    document.body.appendChild(script)

    script.onload = () => {
      nextTick(() => {
        initialize()
      })
    }
  }
}

// Get Square integration ready
const initialize = async () => {
  const appId = props.integration.data.applicationID
  const locationId = props.integration.data.locationID

  try {
    // Initialise Web Payments SDK
    payments.value = window.Square.payments(appId, locationId)

    // Initialise Card payment method
    if (props.integration.data.payment_option_card) {
      initializeCard()
    }

    // Initialise Google Pay payment method
    if (props.integration.data.payment_option_google_pay) {
      initializeGooglePay()
    }

    // Initialise Apple Pay payment method
    if (props.integration.data.payment_option_apple_pay) {
      initializeApplePay()
    }

    // Initialise After Pay payment method
    if (props.integration.data.payment_option_afterpay) {
      initializeAfterPay()
    }

    loading.value = false
  } catch (e) {
    console.log(e)
    error.value = 'Something went wrong while loading Square.'
  }
}

const buildPaymentRequest = (payments) => {
  const amount = Number(props.amount || 0).toFixed(2).toString()
  const currencyCode = props.integration.data.currency
  const countryCode = props.integration.data.country

  const paymentRequest = payments.paymentRequest({
    requestShippingContact: false,
    countryCode,
    currencyCode,
    total: {
      amount,
      label: 'Total',
    },
  });

  return paymentRequest
}

// Initialise Card payment method and attach to DOM
const initializeCard = async () => {
  card = await payments.value.card({
    style: {
      input: {
        fontSize: '13px'
      }
    }
  })

  await setTimeout(() => card.attach('#card-container', 200))
}

// Initialise Google Pay
const initializeGooglePay = async () => {
  const browser = Bowser.getParser(window.navigator.userAgent)

  if (browser.isBrowser('Chrome')) {
    try {
      const paymentRequest = buildPaymentRequest(payments.value)

      googlePay = await payments.value.googlePay(paymentRequest)
      await googlePay.attach('#google-pay-button', {
        buttonSizeMode: 'fill',
      });
    } catch (e) {
      console.log(e)
    }
  }
}

// Initialise Apple Pay
const initializeApplePay = async () => {
  try {
    const paymentRequest = buildPaymentRequest(payments.value)
    applePay = await payments.value.applePay(paymentRequest)

    return applePay
  } catch (e) {
    console.log(e)
  }
}

// Initialise AfterPay / ClearPay
const initializeAfterPay = async () => {
  try {
    const paymentRequest = buildPaymentRequest(payments.value)
    afterpay = await payments.value.afterpayClearpay(paymentRequest)
    await afterpay.attach('#afterpay-button')
  } catch (e) {
    console.error(e)
  }
}


// Tokenize payment method ready to be sent to server for transaction
const tokenize = async (paymentMethod) => {
  try {
    const tokenResult = await paymentMethod.tokenize()

    if (tokenResult.status === 'OK') {
      return tokenResult.token
    } else {
      // this.$sentry.captureException(tokenResult)
      throw new Error(tokenResult.status)
    }
  } catch (e) {
    console.log(e)
  }
}

// http://dave.bookapp.localhost:3000/book-now/confirm?customer=eyJpZCI6ODE5NDA0MywiZmlyc3ROYW1lIjoiSmFycmVkIiwibGFzdE5hbWUiOiJKZW5raW5zb24iLCJ0aXRsZSI6bnVsbCwiZGlzcGxheU5hbWUiOiJKYXJyZWQgSmVua2luc29uIiwiZW1haWwiOiJqYXJyZWQuamVua2luc29uQGdtYWlsLmNvbSIsImFkZHJlc3MiOiI0MiBXYWxsYWJ5IFdheSIsImFkZHJlc3MyIjoiIiwic3VidXJiIjoiIiwiY2l0eSI6IlN5ZG5leSIsInJlZ2lvbiI6Ik5ldyBTb3V0aCBXYWxlc3dlcndlciIsImNvdW50cnkiOiJBdXN0cmFsaWEiLCJhZ2VWZXJpZmllZCI6ZmFsc2V9&slot=UXFzQkpSWUpDaWdwQVdrR1IrNG5SLzk2ME5vYWQrZldBRjFvTHRienp0TjNXWi9MenR3MURjaWxCQ1VyVW1oWENvL2NrNzl5Ymx4LzVrOVBHclF4UEdadmUxNGRUNlZJZG0waVlzcEZTOFkveTBDRkVrUzdqSGd6V0VGanpNRkgzZnhCMGs1UzJDVG9kcHZ2N0xkbStTYmNzSHJuYkpUZklWdGxMVEhSTlhzeUpZOTVTbTQ2bk1rNkZHSWZmeDJrVXgza0VRNndCREtUUlVUK2Q3NTRyRVp0YlFjeFJuSmNmTnRzUTdPeEFXRT0

const verifyBuyer = async (payments, token) => {
  try {
    const intent = (props.intent === 'store') ? 'STORE' : 'CHARGE'
    const amount = props.amount.toString()
    const currencyCode = props.integration.data.currency

    /* const billingContact = {
      givenName: props.customer.firstName,
      familyName: props.customer.lastName
    } */

    const verificationDetails = {
      amount,
      billingContact: JSON.parse(JSON.stringify(billingContact.value)),
      currencyCode,
      intent
    }

    const verificationResults = await payments.verifyBuyer(token, verificationDetails)

    return verificationResults.token
  } catch (e) {
    console.log(e)
  }
}
</script>

 <style>
  #apple-pay-button {
    height: 40px;
    width: 100%;
    -webkit-appearance: -apple-pay-button;
    -apple-pay-button-type: plain;
    -apple-pay-button-style: black;
  }

  #google-pay-button button.gpay-button.black.book.new_style.en {
    border-radius: 6px;
    border: none;
    padding: 9px 15%;
    min-height: 40px;
  }
 </style>

